import React from 'react';
import {
  IconButton,
  SapphireStyleProps,
  useThemeCheck,
  PopoverTrigger,
} from '../..';
import { AriaLabelingProps, FocusableRef } from '@react-types/shared';
import { OverlayTriggerProps } from '@react-types/overlays';
import { AriaPositionProps } from '@react-aria/overlays';
import styles from '@danske/sapphire-css/components/contextualHelp/contextualHelp.module.css';
import { Help, Information } from '@danske/sapphire-icons/react';
import clsx from 'clsx';
import { GlobalDomAttributes } from '../../utils/types';

export type HelpButtonProps = GlobalDomAttributes &
  SapphireStyleProps &
  AriaLabelingProps &
  OverlayTriggerProps & {
    /**
     *
     * The type of contextual help which determines the icon shown.
     *
     * Use "info" for brief, contextual supplemental information. The content
     * inside should have an instructive tone.
     *
     * Use "help" variant to signal more in-depth guidance about a task or UI,
     * this may include links or images or other helpful media. The content
     * inside should have a helpful tone.
     *
     * @default 'info'
     */
    variant?: 'info' | 'help';

    /**
     * The label for the trigger of the contextual help dialog.
     * This will NOT be rendered on the screen, but it is required
     * for accessibility reasons.
     */
    'aria-label': string;

    /**
     * The header inside the popup.
     * This is strongly recommended because having a title to the contextual
     * help content always creates a better experience.
     *
     * Most often you want to use the {@link HelpButtonHeader}.
     */
    contentHeader?: React.ReactNode;

    /**
     * The content inside the popup.
     */
    contentBody: React.ReactNode;

    /**
     * The footer section of the dialog. This should ideally contain links to
     * more related information.
     */
    contentFooter?: React.ReactNode;

    /**
     * Placement of contextual help popover relative to the icon that triggers it.
     */
    placement?: AriaPositionProps['placement'];

    /**
     * If true, the contextual help popover won't have a max width.
     * @default false
     */
    noMaxWidth?: boolean;
  };

export const HelpButton = React.forwardRef(function HelpButton(
  {
    contentHeader,
    contentBody,
    contentFooter,
    placement = 'top',
    variant = 'info',
    noMaxWidth = false,
    // props from OverlayTriggerProps
    isOpen,
    defaultOpen,
    onOpenChange,
    // rest of props contain SapphireStyleProps and AriaLabelingProps
    ...otherProps
  }: HelpButtonProps,
  ref: FocusableRef<HTMLButtonElement>
) {
  useThemeCheck();

  return (
    <PopoverTrigger
      ref={ref}
      noMaxWidth={noMaxWidth}
      popoverContent={({ titleProps }) => (
        <div
          className={clsx(styles['sapphire-contextual-help'], {
            [styles['sapphire-contextual-help--no-max-width']]: noMaxWidth,
          })}
        >
          {contentHeader && (
            <div
              {...titleProps}
              className={styles['sapphire-contextual-help__header']}
            >
              {contentHeader}
            </div>
          )}
          {contentBody}
          {contentFooter && (
            <div className={styles['sapphire-contextual-help__footer']}>
              {contentFooter}
            </div>
          )}
        </div>
      )}
      isOpen={isOpen}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      placement={placement}
      noPadding
    >
      <IconButton {...otherProps} size="xs" variant="tertiary">
        {variant === 'help' ? <Help /> : <Information />}
      </IconButton>
    </PopoverTrigger>
  );
});
